import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Globe, 
  CreditCard, 
  Trash2, 
  AlertCircle,
  ChevronLeft
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const Settings = ({ user, onDeleteAccount, onSubscriptionChange }) => {
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [isLoading, setIsLoading] = useState(true);
  const hasInitialized = useRef(false);
  
  // Store the callback in a ref to avoid dependency issues
  const onSubscriptionChangeRef = useRef(onSubscriptionChange);
  
  useEffect(() => {
    // Simple one-time fetch on mount
    const fetchSubscription = async () => {
      try {
        await onSubscriptionChangeRef.current();
      } catch (error) {
        console.error('Error fetching subscription:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscription();
  }, []); // Empty dependency array = run once on mount

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Español' },
    { code: 'fr', name: 'Français' },
    { code: 'de', name: 'Deutsch' },
    // Add more languages as needed
  ];

  const handleLanguageChange = (languageCode) => {
    setSelectedLanguage(languageCode);
    // Implement language change logic
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    return new Date(isoString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getSubscriptionInfo = () => {
    const sub = user?.subscription;
    if (!sub || sub.plan === 'Free') {
      return {
        plan: 'Free',
        description: 'Basic features with limited usage',
        uploadsUsed: sub?.uploadsThisMonth || 0,
        uploadLimit: 50,
        uploadsRemaining: 50 - (sub?.uploadsThisMonth || 0)
      };
    }

    return {
      plan: sub.plan,
      status: sub.status,
      validUntil: formatDate(sub.validUntil),
      description: sub.status === 'active' ? 
        `Valid until ${formatDate(sub.validUntil)}` :
        'Subscription inactive',
      uploadsUsed: sub.uploadsThisMonth || 0,
      uploadLimit: null // unlimited
    };
  };

  const subInfo = getSubscriptionInfo();

  return (
    <div className="min-h-screen bg-[#fbfbfd] dark:bg-[#0d1117]">
      {/* Background Effects - Dark Mode Only */}
      <div className="absolute inset-0 dark:block hidden pointer-events-none">
        {/* Gradient Orbs */}
        <div className="absolute top-0 -left-4 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[100px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-indigo-500/5 rounded-full blur-[120px]" />
      </div>

      <div className="relative max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center mb-8">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg mr-4 
              text-gray-600 dark:text-gray-300
              hover:bg-white/50 dark:hover:bg-gray-800/50 
              transition-all duration-200"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Settings
          </h1>
        </div>

        {/* Settings Sections */}
        <div className="space-y-6">
          {/* Language Settings */}
          <div className="bg-white/90 dark:bg-gray-900/40 backdrop-blur-xl 
            rounded-2xl p-6 shadow-[0_0_1px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.05),0_8px_16px_rgba(0,0,0,0.05)]
            dark:shadow-glow dark:border dark:border-gray-800/50 border border-gray-200/50">
            <div className="flex items-center mb-4">
              <Globe className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400" />
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                Language
              </h2>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`p-3 rounded-lg border transition-all
                    ${selectedLanguage === lang.code
                      ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                      : 'border-gray-200/50 dark:border-gray-700/50 text-gray-600 dark:text-gray-300 hover:border-blue-500/50'
                    }`}
                >
                  <span className="text-sm font-medium">
                    {lang.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Subscription Settings */}
          <div className="bg-white/90 dark:bg-gray-900/40 backdrop-blur-xl 
            rounded-2xl p-6 shadow-[0_0_1px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.05),0_8px_16px_rgba(0,0,0,0.05)]
            dark:shadow-glow dark:border dark:border-gray-800/50 border border-gray-200/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CreditCard className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400" />
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                  Subscription
                </h2>
              </div>
              {subInfo.plan === 'Free' && (
                <button
                  onClick={() => navigate('/subscription')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg
                    hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl
                    transform hover:scale-105 transition-all duration-200"
                >
                  Upgrade Plan
                </button>
              )}
            </div>
            {isLoading ? (
              <div className="mt-4 flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent" />
                <span className="text-sm text-gray-500 dark:text-gray-400">Loading subscription info...</span>
              </div>
            ) : (
              <div className="mt-2 space-y-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Current plan: {subInfo.plan}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {subInfo.description}
                </p>
                {subInfo.status && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Status: {subInfo.status}
                  </p>
                )}
                <div className="mt-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Documents uploaded this month: {subInfo.uploadsUsed}
                    {subInfo.uploadLimit ? ` / ${subInfo.uploadLimit}` : ' (unlimited)'}
                  </p>
                  {subInfo.uploadLimit && (
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(subInfo.uploadsUsed / subInfo.uploadLimit) * 100}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Danger Zone */}
          <div className="bg-white/90 dark:bg-gray-900/40 backdrop-blur-xl 
            rounded-2xl p-6 shadow-[0_0_1px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.05),0_8px_16px_rgba(0,0,0,0.05)]
            dark:shadow-glow border-red-200/50 dark:border-red-800/50 border">
            <div className="flex items-center mb-4">
              <AlertCircle className="w-5 h-5 mr-2 text-red-500" />
              <h2 className="text-lg font-medium text-red-600 dark:text-red-400">
                Danger Zone
              </h2>
            </div>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 bg-red-100/50 dark:bg-red-900/20 
                text-red-600 dark:text-red-400 rounded-lg
                hover:bg-red-200/50 dark:hover:bg-red-900/40 
                transition-all duration-200 flex items-center
                border border-red-200/50 dark:border-red-800/50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Account
            </button>
          </div>
        </div>
      </div>

      {/* Delete Account Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl 
          border border-gray-200/20 dark:border-gray-700/20 rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-900 dark:text-white">
              Delete Account
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500 dark:text-gray-400">
              This action cannot be undone. This will permanently delete your
              account and remove all of your data from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-100 dark:bg-gray-800 
              text-gray-900 dark:text-white border-gray-200 dark:border-gray-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={onDeleteAccount}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600
                text-white border-none"
            >
              Delete Account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Settings; 